import { ApolloClient } from '@apollo/client';
import { isNil, throttle } from 'lodash';
import * as ms from 'milliseconds';
import { Mutations } from '@shared/api';
import { getStore } from '@shared/store';
import { API } from '@shared/types/api';

export const DEFAULT_MIME_TYPE = 'application/octet-stream';

interface UploadPolicy {
  uploadUrl: string;
  fields: Record<string, string>;
}

export interface FileToUpload {
  id: string;
  url: string;
  name: string;
  contentType: string;
}

export async function signFileForUpload(
  client: ApolloClient<unknown>,
  file: FileToUpload,
): Promise<UploadPolicy | undefined> {
  const fileName = file.name?.replace(/[^a-z0-9._-]+/gi, '') || 'file';
  const mimeType = file.contentType || DEFAULT_MIME_TYPE;
  const { data } = await client.mutate<API.CreateMediaMutation, API.CreateMediaMutationVariables>({
    mutation: Mutations.business.createMedia,
    variables: {
      input: {
        fileName,
        mimeType,
      },
    },
  });

  const uploadUrl = data?.createMedia.presignedPost.url;
  const fields = data?.createMedia.presignedPost.fields;

  if (isNil(uploadUrl) || isNil(fields)) {
    return undefined;
  }

  return {
    uploadUrl,
    fields,
  };
}

export interface UploadedFile {
  /** The full public url of the attachment, which is the bucket url with `key` appended. */
  url: string;
  /**
   * S3 object key, shaped `<uuid>/<fileName>`.
   *
   * Kept alongside the url because the two are not interchangeable: rendering needs a URL, while
   * the Ask gateway addresses attachments by key alone (see `AskPromptAttachment`). The key cannot
   * be recovered from the url afterwards without knowing the bucket url, which nothing downstream
   * holds — so anything that needs it has to take it from here.
   */
  key: string;
}

/*
    Upload a file (chat message attachment) through the API. Returns the full
    public url of the attachment, and the S3 object key it was stored under.
*/
export async function uploadFileWithKey(
  client: ApolloClient<unknown>,
  file: FileToUpload,
): Promise<UploadedFile> {
  // Make the file loading indicator show right away
  updateFileUploadProgress(file.id, 0);

  const formData = new FormData();
  const policy = await signFileForUpload(client, file).catch(() => null);
  if (!policy) {
    throw new Error('Failed to sign file for upload');
  }

  const { uploadUrl, fields } = policy;

  Object.keys(fields).forEach(key => {
    formData.append(key, fields[key]);
  });

  formData.append('Content-Type', file.contentType || DEFAULT_MIME_TYPE);

  // Web requires a Blob; create from object URL
  const blob = await fetch(file.url).then(res => res.blob());
  formData.append('file', blob);

  const key = String((fields as any)['Key'] ?? '');

  return await new Promise<UploadedFile>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // For reference:
    // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest
    // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/upload
    xhr.upload.addEventListener(
      'progress',
      throttle((e: any) => {
        // Get upload progress (https://gist.github.com/domenic/2138293)
        if (e.lengthComputable) {
          const progress = e.loaded / e.total;
          updateFileUploadProgress(file.id, progress);
        }
      }, ms.seconds(0.2)),
    );
    xhr.onload = () => {
      if (xhr.status !== 204) {
        return reject(new Error(`Upload failed with status ${xhr.status}`));
      }
      resolve({ url: `${uploadUrl}${key}`, key });
    };
    xhr.addEventListener('error', () => reject(new Error('XHR error')));
    xhr.addEventListener('abort', () => reject(new Error('XHR aborted')));

    xhr.open('POST', uploadUrl, true);
    xhr.send(formData);
  });
}

/*
    Upload a file (chat message attachment) through the API. Returns the full
    public url of the attachment. Use `uploadFileWithKey` when the S3 object key
    is needed as well.
*/
export async function uploadFile(
  client: ApolloClient<unknown>,
  file: FileToUpload,
): Promise<string> {
  const { url } = await uploadFileWithKey(client, file);
  return url;
}

export function updateFileUploadProgress(fileKey: string, progress: number) {
  const store = getStore();
  return store.dispatch({
    type: 'UPLOAD:UPDATE_FILE_UPLOAD_PROGRESS',
    value: { fileKey, progress },
  });
}
